/**
 * Date utility functions for consistent date calculations across the application
 */

/**
 * Get the start of the current week (Monday)
 * @returns Date object representing the start of the current week
 */
export function getStartOfCurrentWeek(): Date {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  // Calculate days to subtract to get to Monday (1)
  // If today is Sunday (0), we subtract 6 days to get to last Monday
  // If today is Monday (1), we subtract 0 days
  // If today is Tuesday (2), we subtract 1 day, etc.
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - daysToSubtract);
  startOfWeek.setHours(0, 0, 0, 0);

  return startOfWeek;
}

/**
 * Get the end of the current week (Sunday)
 * @returns Date object representing the end of the current week
 */
export function getEndOfCurrentWeek(): Date {
  const startOfWeek = getStartOfCurrentWeek();
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  return endOfWeek;
}

/**
 * Get the start of the current month (1st day)
 * @returns Date object representing the start of the current month
 */
export function getStartOfCurrentMonth(): Date {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  startOfMonth.setHours(0, 0, 0, 0);
  return startOfMonth;
}

/**
 * Get the end of the current month (last day)
 * @returns Date object representing the end of the current month
 */
export function getEndOfCurrentMonth(): Date {
  const today = new Date();
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);
  return endOfMonth;
}

/**
 * Check if a date falls within the current week
 * @param date - The date to check
 * @returns True if the date is within the current week
 */
export function isDateInCurrentWeek(date: Date): boolean {
  const startOfWeek = getStartOfCurrentWeek();
  const endOfWeek = getEndOfCurrentWeek();

  return date >= startOfWeek && date <= endOfWeek;
}

/**
 * Check if a date falls within the current month
 * @param date - The date to check
 * @returns True if the date is within the current month
 */
export function isDateInCurrentMonth(date: Date): boolean {
  const startOfMonth = getStartOfCurrentMonth();
  const endOfMonth = getEndOfCurrentMonth();

  return date >= startOfMonth && date <= endOfMonth;
}
